<template>
  <div class="pir">
    <p class="pir__description">{{ $t('resources.product.description') }}</p>

    <p v-if="!rules.length" class="pir__empty">{{ $t('resources.product.no_rules') }}</p>

    <div v-for="(rule, idx) in rules" :key="rule._key" class="pir__rule">
      <div class="pir__rule-grid">
        <div class="form-group">
          <label :for="`pir_target_${idx}`">{{ $t('resources.product.category') }}</label>
          <select :id="`pir_target_${idx}`" v-model="rule.target" @change="markDirty">
            <optgroup :label="$t('resources.product.category')">
              <option v-for="cat in categories" :key="'c:' + cat" :value="'category:' + cat">
                {{ cat }}
              </option>
            </optgroup>
            <optgroup :label="$t('resources.product.specific')">
              <option v-for="res in resources" :key="'r:' + res.id" :value="'resource:' + res.id">
                {{ res.name }}
                <template v-if="res.code">({{ res.code }})</template>
              </option>
            </optgroup>
          </select>
        </div>
        <div class="form-group">
          <label :for="`pir_qty_${idx}`">{{ $t('resources.product.quantity') }}</label>
          <input
            :id="`pir_qty_${idx}`"
            type="number"
            min="1"
            v-model.number="rule.quantity"
            @input="markDirty"
          />
        </div>
        <div class="form-group">
          <label :for="`pir_before_${idx}`">{{ $t('resources.product.days_before') }}</label>
          <input
            :id="`pir_before_${idx}`"
            type="number"
            min="0"
            v-model.number="rule.days_before"
            @input="markDirty"
          />
        </div>
        <div class="form-group">
          <label :for="`pir_after_${idx}`">{{ $t('resources.product.days_after') }}</label>
          <input
            :id="`pir_after_${idx}`"
            type="number"
            min="0"
            v-model.number="rule.days_after"
            @input="markDirty"
          />
        </div>
        <div class="form-group">
          <label :for="`pir_height_${idx}`">
            {{ $t('resources.product.min_height') }}
            <span
              class="form-group__info"
              v-tippy="{ content: $t('resources.product.min_height_hint') }"
            >
              <BaseIcon name="info" size="12" />
            </span>
          </label>
          <input
            :id="`pir_height_${idx}`"
            type="number"
            min="0"
            step="100"
            v-model.number="rule.min_height_mm"
            placeholder="—"
            @input="markDirty"
          />
        </div>
        <button
          type="button"
          class="pir__remove"
          :title="$t('common.delete')"
          @click="removeRule(idx)"
        >
          <BaseIcon name="trash" size="14" />
        </button>
      </div>
    </div>

    <div class="pir__actions">
      <Btn type="button" variant="outlined" size="sm" icon="plus" @click="addRule">
        {{ $t('resources.product.add_rule') }}
      </Btn>
      <Btn v-if="dirty" type="button" variant="primary" size="sm" :loading="saving" @click="save">
        {{ $t('common.save') }}
      </Btn>
    </div>
  </div>
</template>

<script>
  import { defineComponent, ref, watch, onMounted, getCurrentInstance } from 'vue';
  import { useToastStore } from '@/store/toastStore';
  import { resourceService } from '@/services/resourceService';
  import { updateProductInFeed } from '@shared/services/productService';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  let keyCounter = 0;

  const toRow = (r) => ({
    _key: ++keyCounter,
    target: r.resource_id ? `resource:${r.resource_id}` : `category:${r.category || ''}`,
    quantity: Number(r.quantity) || 1,
    days_before: Number(r.days_before) || 0,
    days_after: Number(r.days_after) || 0,
    min_height_mm: r.min_height_mm != null ? Number(r.min_height_mm) : null,
  });

  export default defineComponent({
    name: 'ProductInstallationRequirements',
    components: { Btn, BaseIcon },
    props: {
      productId: { type: [String, Number], required: true },
      modelValue: { type: Array, default: () => [] },
    },
    emits: ['update:modelValue', 'saved'],
    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const toastStore = useToastStore();

      const rules = ref((props.modelValue || []).map(toRow));
      const categories = ref([]);
      const resources = ref([]);
      const dirty = ref(false);
      const saving = ref(false);

      watch(
        () => props.modelValue,
        (next) => {
          if (!dirty.value) rules.value = (next || []).map(toRow);
        }
      );

      onMounted(async () => {
        const [cats, res] = await Promise.allSettled([
          resourceService.getCategories(),
          resourceService.getAll(),
        ]);
        categories.value = cats.status === 'fulfilled' ? cats.value : [];
        resources.value =
          res.status === 'fulfilled' ? res.value.filter((r) => r.kind !== 'vehicle') : [];
        // Keep categories referenced by existing rules selectable even if no resource has them yet.
        for (const rule of rules.value) {
          if (rule.target.startsWith('category:')) {
            const cat = rule.target.slice('category:'.length);
            if (cat && !categories.value.includes(cat)) categories.value.push(cat);
          }
        }
      });

      const markDirty = () => {
        dirty.value = true;
      };

      const addRule = () => {
        rules.value.push({
          _key: ++keyCounter,
          target: categories.value.length ? `category:${categories.value[0]}` : '',
          quantity: 1,
          days_before: 0,
          days_after: 0,
          min_height_mm: null,
        });
        dirty.value = true;
      };

      const removeRule = (idx) => {
        rules.value.splice(idx, 1);
        dirty.value = true;
      };

      const serialize = () =>
        rules.value
          .filter((r) => r.target)
          .map((r) => {
            const sep = r.target.indexOf(':');
            const kind = r.target.slice(0, sep);
            const value = r.target.slice(sep + 1);
            return {
              resource_id: kind === 'resource' ? value : null,
              category: kind === 'category' ? value : null,
              quantity: Math.max(1, Number(r.quantity) || 1),
              days_before: Math.max(0, Number(r.days_before) || 0),
              days_after: Math.max(0, Number(r.days_after) || 0),
              min_height_mm:
                r.min_height_mm === null || r.min_height_mm === '' ? null : Number(r.min_height_mm),
            };
          });

      const save = async () => {
        saving.value = true;
        try {
          const payload = serialize();
          await updateProductInFeed(null, props.productId, { installationRequirements: payload });
          dirty.value = false;
          emit('update:modelValue', payload);
          emit('saved', payload);
          toastStore.addToast({ type: 'success', message: $t('resources.product.saved') });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: error?.response?.data?.error || $t('resources.product.save_failed'),
          });
        } finally {
          saving.value = false;
        }
      };

      return { rules, categories, resources, dirty, saving, markDirty, addRule, removeRule, save };
    },
  });
</script>

<style scoped lang="scss">
  .pir {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    &__description,
    &__empty {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__rule {
      padding: 0.625rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 8px;
      background: var(--slate-50);
    }

    &__rule-grid {
      display: grid;
      grid-template-columns: 2fr 0.7fr 0.8fr 0.8fr 1fr auto;
      gap: 0.5rem;
      align-items: end;

      .form-group {
        margin: 0;
      }

      @include lg-down {
        grid-template-columns: 1fr 1fr;
      }
    }

    &__remove {
      height: 38px;
      width: 32px;
      border: none;
      background: none;
      color: var(--slate-400);
      cursor: pointer;
      border-radius: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      @include hover {
        color: var(--red-600);
        background: var(--red-50);
      }
    }

    &__actions {
      display: flex;
      gap: 0.5rem;
      align-items: center;
    }
  }
</style>
